import { useInfiniteQuery } from "@tanstack/react-query";
import { USERAXIOS } from "../../api";
import { Resume } from "../../types/resumes-managment/resume";
import { useMemo } from "react";

interface ResumesResponse {
  data: Resume[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

interface ResumesFilters {
  page?: number;
  searchText?: string;
  requested_resume_language?: string;
  desired_work_department?: string;
  country_id?: string;
  city_id?: string;
  targeted_job_type?: string;
  foreign_languages?: string;
  gender?: string;
  military_service?: string;
  photo_included?: string;
  status?: string;
}

export const useResumes = () => {
  return useInfiniteQuery({
    queryKey: ["resumes"],
    queryFn: async ({ pageParam = 1 }): Promise<ResumesResponse> => {
      const response = await USERAXIOS.get<ResumesResponse>("/resumes", {
        params: {
          page: pageParam,
          per_page: 100,
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined;
    },
    initialPageParam: 1,
  });
};

export const useResumesInfinite = (
  pageSize: number = 20,
  searchTerm: string = ""
) => {
  return useInfiniteQuery({
    queryKey: ["resumes", "infinite", pageSize, searchTerm],
    queryFn: async ({ pageParam = 1 }): Promise<ResumesResponse> => {
      const response = await USERAXIOS.get<ResumesResponse>(
        `/resumes?title=${searchTerm}&page=${pageParam}&per_page=${pageSize}`
      );
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined;
    },
    initialPageParam: 1,
  });
};

// Hook for autocomplete usage
export const useResumesForAutocomplete = (
  pageSize: number = 20,
  searchTerm: string = ""
) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["resumes", "infinite", pageSize, searchTerm],
    queryFn: async ({ pageParam = 1 }): Promise<ResumesResponse> => {
      const response = await USERAXIOS.get<ResumesResponse>(`/resumes`, {
        params: {
          page: pageParam,
          per_page: pageSize,
          title: searchTerm || undefined,
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined;
    },
    initialPageParam: 1,
  });

  // Flatten all pages into a single array
  const resumes = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) || [];
  }, [data]);

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return {
    items: resumes,
    loading: isLoading || isFetchingNextPage,
    hasMore: hasNextPage,
    loadMore,
    error,
    refetch,
    totalPages: data?.pages[0]?.meta.last_page || 0,
    totalItems: data?.pages[0]?.meta.total || 0,
  };
};
